
import { Vue, Prop, Component, Ref } from "vue-property-decorator";
import notifications from "@/lib/notifications";
import {
  AgentResource,
  AgentFacilityResource,
  AgentManufacturerResource,
  AgentCompanyResource
} from "../../../models/api/system/agents";
import { ReasonCodeResource } from "../../../models/api/system/reasonCodes";
import { getModule } from "vuex-module-decorators";
import SystemModule from "@/store/modules/system";
import permissions from "@/security/permissions";
import router from "@/router";

const systemModule = getModule(SystemModule);

interface AgentManufacturerResourceSelect {
  manufacturer: AgentManufacturerResource;
  isSelected: boolean;
}

interface AgentFacilityResourceSelect {
  facility: AgentFacilityResource;
  isSelected: boolean;
}

@Component({
  components: {}
})
export default class ManageBillToCompanies extends Vue {
  @Prop()
  agent: AgentResource;

  @Prop()
  public agentFacilities: Array<AgentFacilityResource>;

  @Prop()
  public agentManufacturers: Array<AgentManufacturerResource>;

  @Prop()
  public agentCompanies: Array<AgentCompanyResource>;

  @Ref() readonly validator!: any;

  filterString: string = "";
  loading: boolean = false;
  loadingPage: boolean = false;
  isSaving: boolean = false;
  manufacturerFilterString: string = "";
  facilityFilterString: string = "";
  reasonCodeFilterString: string = "";

  agentCompanyManufacturers: AgentManufacturerResourceSelect[] = [];
  agentCompanyFacilities: AgentFacilityResourceSelect[] = [];
  agentCompanyReasonCodes: ReasonCodeResource[] = [];

   agentCompany: AgentCompanyResource = {
    id: null,
    agentId: this.agent?.id,
    companyId: null,
    agentCompanyId: null,
    requireSignature: null,
    requirePhoto: null,
    isBillTo: false,
    isConsignee: false,
    isShipper: false,
    isActive: false,
    name: null,
    address1: null,
    address2: null,
    city: null,
    state: null,
    zip: null,
    primaryPhone: null,
    secondaryPhone: null,
    manufacturerCodes: [],
    facilityCodes: [],
    reasonCodes: []
  };

  isCompanyModalVisible: boolean = false;
  isDeleteCompanyModalVisible: boolean = false;

  reasonCode: ReasonCodeResource = {
    id: null,
    code: null,
    description: null
  };

  async IsFormValid() {
    return await this.validator.validate();
  }

  async validateFormForSave() {
    return this.validator.validate()
      .then(isValid => {
        if(isValid) {
          this.saveAgentCompany()
        }
    });
  };

  addAgentCompany() {
    this.agentCompany = {
      id: null,
      agentId: this.agent.id,
      companyId: null,
      agentCompanyId: null,
      requireSignature: false,
      requirePhoto: false,
      isBillTo: true,
      isConsignee: true,
      isShipper: true,
      isActive: true,
      name: null,
      address1: null,
      address2: null,
      city: null,
      state: null,
      zip: null,
      primaryPhone: null,
      secondaryPhone: null,
      manufacturerCodes: [],
      facilityCodes: [],
      reasonCodes: []
    };
    this.reasonCode = {
      id: null,
      code: null,
      description: null
    };

    this.agentCompanyManufacturers = this.agentManufacturers
      .filter(x => x.isActive == true)
      
      .map(x => ({ manufacturer: x, isSelected: false }));
    this.agentCompanyFacilities = this.agentFacilities.map(x => ({
      facility: x,
      isSelected: false
    }))
    this.agentCompanyReasonCodes = [];

    this.isCompanyModalVisible = true;
  }

  async saveAgentCompany() {
    try {
      this.isSaving = true;
      this.agentCompany.agentId = this.agent.id;
      this.agentCompany.manufacturerCodes = this.agentCompanyManufacturers
        .filter(x => x.isSelected == true)
        .map(x => x.manufacturer.manufacturerCode);
      this.agentCompany.facilityCodes = this.agentCompanyFacilities
        .filter(x => x.isSelected == true)
        .map(x => x.facility.facilityCode);
      this.agentCompany.reasonCodes = this.agentCompanyReasonCodes;

      if (this.agentCompany.id === null) {
        await systemModule.postAgentCompany(this.agentCompany);
      } else {
        await systemModule.putAgentCompany(this.agentCompany);
      }
      notifications.success("Success", `Company saved.`);
    } catch (error) {
      notifications.error("Save Failed", "Unable to save company.", error);
    } finally {
      this.isCompanyModalVisible = false;
      this.isSaving = false;
    }

    this.$emit("updated");
  }

  editAgentCompany(companyId: string) {
    let clone = this.agentCompanies.find(x => x.companyId === companyId);
    clone = { ...clone };
    this.agentCompany = clone;

    this.reasonCode = {
      id: null,
      code: null,
      description: null
    };
    this.agentCompanyManufacturers = this.agentManufacturers
      .filter(x => x.isActive == true)
      .map(x => ({
        manufacturer: x,
        isSelected: this.agentCompany.manufacturerCodes.includes(x.manufacturerCode)
      }))
    .sort( (a, b) => {
      if(a.isSelected && !b.isSelected) return -1;
      if(!a.isSelected && b.isSelected) return 1;
      return 0;
    } );
    
    this.agentCompanyFacilities = this.agentFacilities.map(x => ({
      facility: x,
      isSelected: this.agentCompany.facilityCodes.includes(x.facilityCode)
    }))
    .sort( (a, b) => {
      if(a.isSelected && !b.isSelected) return -1;
      if(!a.isSelected && b.isSelected) return 1;
      return 0;
    } );


    this.agentCompanyReasonCodes = this.agentCompany.reasonCodes;

    this.isCompanyModalVisible = true;
  }

  done() {
    router.push(`/admin/agents`);
  }

  addReasonCode() {
    this.agentCompanyReasonCodes.push(this.reasonCode);
    this.reasonCode = {
      id: null,
      code: null,
      description: null
    };
  }

  removeReasonCode(reasonCode: ReasonCodeResource) {
    this.agentCompanyReasonCodes = this.agentCompanyReasonCodes.filter(x => x !== reasonCode);
  }
}
